import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import {
  Table,
  TableColumnConfig,
  EditIcon,
  DeleteIcon,
  Chip,
  CheckIconSmall,
  XIconSmall,
  Truncated,
  Text,
} from '@frontend/design-system';
import { EmptyState } from './empty-state';
import { RecurrenceType, TimeUnit } from './types';

interface AvailabilityTemplate {
  id: string;
  recurrence: RecurrenceType;
  days: string[];
  startTime: string;
  endTime: string;
  appointmentTypes: string[];
  providers: string[];
  selfBookable: boolean;
  startDate: string;
  interval?: number;
  timeUnit?: TimeUnit;
}

export const AvailabilityTable = () => {
  const { t } = useTranslation('schedule-settings');

  const colConfig: TableColumnConfig<AvailabilityTemplate>[] = [
    {
      id: 'recurrence',
      Header: t('Recurrence'),
      accessor: 'recurrence',
      disableSortBy: true,
      minWidth: 120,
      cellRenderer: (cellValue: string) => {
        return <Text css={{ textTransform: 'capitalize' }}>{t(`{{text}}`, { text: cellValue })}</Text>;
      },
    },
    {
      id: 'days',
      Header: t('Day(s)'),
      accessor: 'days',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (_, rowData) => {
        const startDate = rowData?.startDate ? dayjs(rowData?.startDate).format('MMM D, YYYY') : '';
        if (rowData?.recurrence === 'specific date') {
          return <Text>{`${startDate}`}</Text>;
        }
        if (rowData?.recurrence === 'custom' && rowData?.interval && rowData?.timeUnit) {
          return (
            <Text>
              {t(`Every {{interval}} {{timeUnit}} starting from {{startDate}}`, {
                interval: rowData.interval,
                timeUnit: rowData.timeUnit,
                startDate,
              })}
            </Text>
          );
        }
        if (rowData?.recurrence === 'weekly') {
          const days = rowData?.days || [];
          const text = days
            .map((day) => {
              day = day.charAt(0).toUpperCase() + day.slice(1, 3);
              return day;
            })
            .join(', ');
          return <Truncated>{t(`{{text}}`, { text })}</Truncated>;
        }
        return <Text>-</Text>;
      },
    },
    {
      id: 'hours',
      Header: t('Hours'),
      disableSortBy: true,
      minWidth: 150,
      cellRenderer: (_, rowData) => {
        const startTime = dayjs(rowData?.startTime, 'HH:mm').format('h:mm A');
        const endTime = dayjs(rowData?.endTime, 'HH:mm').format('h:mm A');
        return `${startTime} - ${endTime}`;
      },
    },
    {
      id: 'appointmentTypes',
      Header: t('Appointment Types'),
      accessor: 'appointmentTypes',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue: string[]) => {
        const text = cellValue.map((type) => type).join(', ');
        return <Truncated>{t(`{{text}}`, { text })}</Truncated>;
      },
    },
    {
      id: 'providers',
      Header: t('Provider(s)'),
      accessor: 'providers',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue: string[]) => {
        const text = cellValue.map((provider) => provider).join(', ');
        return <Truncated>{text}</Truncated>;
      },
    },
    {
      id: 'selfBookable',
      Header: t('Self Bookable'),
      accessor: 'selfBookable',
      disableSortBy: true,
      maxWidth: 150,
      cellRenderer: (cellValue: boolean) => (
        <span>
          {cellValue ? (
            <Chip leftElement={<CheckIconSmall />} variant='success'>
              {t('Yes')}
            </Chip>
          ) : (
            <Chip leftElement={<XIconSmall />} variant='rose'>
              {t('No')}
            </Chip>
          )}
        </span>
      ),
    },
  ];

  return (
    <Table
      colConfig={colConfig}
      data={mockData}
      tableStyle={tableStyle}
      disableMultiSort
      emptyStateConfig={<EmptyState description={t('No availability templates found')} />}
      rowActions={{
        actions: [
          {
            label: t('Edit'),
            Icon: EditIcon,
            onClick: (rowData) => console.log('Edit', rowData.id),
          },
          {
            label: t('Delete'),
            Icon: DeleteIcon,
            onClick: (rowData) => console.log('Delete', rowData.id),
          },
        ],
      }}
    />
  );
};

// Don't judge my !important usage here... Table component adds borders that I can't override
const tableStyle = css({
  borderRadius: '0 !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
});

const mockData: AvailabilityTemplate[] = [
  {
    id: '1',
    recurrence: RecurrenceType.CUSTOM,
    days: [],
    startTime: '13:00',
    endTime: '18:00',
    appointmentTypes: ['Consultation', 'Follow-up', 'Consultation', 'Follow-up', 'Consultation', 'Follow-up'],
    providers: ['Dr. Brown', 'Dr. Davis', 'Dr. Martinez'],
    selfBookable: true,
    startDate: '2024-07-01',
    interval: 1,
    timeUnit: 'months',
  },
  {
    id: '2',
    recurrence: RecurrenceType.SPECIFIC_DATE,
    days: [],
    startTime: '08:00',
    endTime: '12:00',
    appointmentTypes: ['Cleaning', 'Exam'],
    providers: ['Dr. Williams'],
    selfBookable: false,
    startDate: '2024-07-01',
  },
  {
    id: '3',
    recurrence: RecurrenceType.CUSTOM,
    days: [],
    startTime: '13:00',
    endTime: '18:00',
    appointmentTypes: ['Consultation', 'Follow-up', 'Consultation', 'Follow-up', 'Consultation', 'Follow-up'],
    providers: ['Dr. Brown', 'Dr. Davis', 'Dr. Martinez'],
    selfBookable: true,
    startDate: '2024-07-01',
    interval: 2,
    timeUnit: 'weeks',
  },
  {
    id: '4',
    recurrence: RecurrenceType.WEEKLY,
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    startTime: '13:00',
    endTime: '18:00',
    appointmentTypes: ['Special Procedures'],
    providers: ['Dr. Smith'],
    selfBookable: false,
    startDate: '2024-07-01',
  },
  {
    id: '5',
    recurrence: RecurrenceType.CUSTOM,
    days: [],
    startTime: '13:00',
    endTime: '18:00',
    appointmentTypes: ['Consultation', 'Follow-up', 'Consultation', 'Follow-up', 'Consultation', 'Follow-up'],
    providers: ['Dr. Brown', 'Dr. Davis', 'Dr. Martinez'],
    selfBookable: true,
    startDate: '2024-07-01',
    interval: 2,
    timeUnit: 'days',
  },
];
